import { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react'

export const TAB_LIST_KEY = 'openTabs'
export const TAB_ID_KEY = 'tabId'

const generateTabId = () => {
  let tabId = sessionStorage.getItem(TAB_ID_KEY)

  // Detect tab duplication via window.name
  // Cannot only rely on sessionStorage as it clones the data across tabs
  if (!window.name) {
    // Assign a new UUID to window.name
    window.name = crypto.randomUUID()
    // Setting window.name to sessionStorage for consistency
    tabId = window.name
    sessionStorage.setItem(TAB_ID_KEY, tabId)
  }

  return tabId
}

const getOpenTabs = (): string[] => {
  try {
    const raw = localStorage.getItem(TAB_LIST_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

const updateOpenTabs = (updateFn: (tabs: string[]) => string[]) => {
  const tabs = updateFn(getOpenTabs())
  localStorage.setItem(TAB_LIST_KEY, JSON.stringify(tabs))
}

const BrowserTabsTrackerContext = createContext<boolean | undefined>(undefined)

export const BrowserTabsTrackerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isMultipleTabsOpen, setIsMultipleTabsOpen] = useState(false)
  const tabId = generateTabId()

  useEffect(() => {
    // Add the current tab to the list of open tabs
    updateOpenTabs((tabs) => {
      const filteredTabs = tabs.filter((id): id is string => id !== null)
      return tabId ? Array.from(new Set([...filteredTabs, tabId])) : Array.from(new Set(filteredTabs))
    })

    const cleanup = () => {
      // Remove the current tab from the list of open tabs
      updateOpenTabs((tabs) => tabs.filter((id) => id !== tabId))
    }

    const onStorage = () => {
      // Get the latest list of open tabs and update the state
      const currentTabs = getOpenTabs()
      if (currentTabs.length === 0) {
        // If TAB_LIST_KEY is cleared, reset the state
        setIsMultipleTabsOpen(false)
      } else {
        setIsMultipleTabsOpen(currentTabs.filter((id) => id !== tabId).length > 0)
      }
    }

    // Listen for the `storage` event to synchronize tabs
    window.addEventListener('storage', onStorage)

    // Perform an initial check
    onStorage()

    // Cleanup on tab close or component unmount
    window.addEventListener('beforeunload', cleanup)
    return () => {
      cleanup()
      window.removeEventListener('beforeunload', cleanup)
      window.removeEventListener('storage', onStorage)
    }
  }, [tabId])

  return <BrowserTabsTrackerContext.Provider value={isMultipleTabsOpen}>{children}</BrowserTabsTrackerContext.Provider>
}

export const useMultipleTabsOpen = () => {
  const context = useContext(BrowserTabsTrackerContext)
  if (context === undefined) {
    throw new Error('useMultipleTabsOpen must be used within a BrowserTabsTrackerProvider')
  }
  return context
}
